import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * AUT-223 — confirmation gate for the two capture actions.
 *
 * Both endpoints take an empty body, so this collects no input; what it does
 * is put the AMOUNT and the consequence in front of the operator before real
 * money moves. Capture is irreversible except by refund, and an operator's
 * slip here is somebody's 1250 BYN.
 *
 * It is also where a pre-flight caveat lands (`warning`) — AUT-301's expired
 * hold window is the first one. That block informs, it does not disable: the
 * confirm button stays live, because past our escrow deadline the server asks
 * the provider and a capture can still legitimately succeed.
 *
 * The dialog is also where a rejection lands (`error`), and it deliberately
 * stays OPEN when one arrives: the four server codes each imply a different
 * next move (refresh / stop, the money already went / run reconcile instead),
 * and that instruction is worthless if it flashes past as a toast while the
 * dialog closes. In-flight the confirm is disabled + spinning and the backdrop
 * can't dismiss.
 *
 * `kind` picks the copy; one component rather than two near-identical files
 * because the only difference between them IS the copy.
 */
export type CaptureDialogKind = 'capture' | 'reconcile';

export interface CaptureConfirmDialogProps {
  open: boolean;
  kind: CaptureDialogKind;
  /** Pre-formatted money string (already carries the DTO's currency). */
  amount: string;
  /**
   * AUT-301 — a caveat that must not gate the action, only inform it. Today
   * that is the hold-window phase: our escrow deadline has passed (or is about
   * to) and the server will ask the provider before it settles. It sits ABOVE
   * the error slot because it is pre-flight context, not a rejection.
   */
  warning?: string | null;
  loading: boolean;
  /** Resolved server-error text; rendered inline, keeps the dialog open. */
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const CaptureConfirmDialog = ({
  open,
  kind,
  amount,
  warning = null,
  loading,
  error,
  onConfirm,
  onCancel,
}: CaptureConfirmDialogProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t(`orders.capture.dialog.${kind}.title`)}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t(`orders.capture.dialog.${kind}.description`, { amount })}
        </DialogContentText>
        {warning !== null ? (
          <Alert severity="warning" data-testid="capture-hold-window-warning" sx={{ mt: 2 }}>
            {warning}
          </Alert>
        ) : null}
        {error !== null ? (
          <Alert severity="error" data-testid="capture-action-error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color={kind === 'capture' ? 'primary' : 'inherit'}
          disabled={loading}
          loading={loading}
          onClick={() => {
            if (!loading) onConfirm();
          }}
        >
          {t(`orders.capture.dialog.${kind}.confirm`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
